import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import ServiceLocationModal from "../../components/Modal/Quote/Admin/Service1stModal";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import "./../../../assets/css/purchase/datatable.css";
import Pagination from "../Pagination";
import SearchableSelect from "../../components/SearchableSelect";
import { formatThaiPhone } from "../../../utils/phoneUtils";

export default function TableCustomer({
  data = [],
  setData,
  onOpenContactModal,
  reloadCustomers,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  totalRecords = 0,
  onFilter,
  activeGroup,
  setActiveGroup,
  todayCount = 0,
  mineCount = 0,
  unassignedCount = 0,
  totalAllRecords = 0,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  currentEmpName = null,
  focusedArId = null,
  onFocusCustomer,
  branchFilter = "all",
  onBranchFilterChange,
  branches = [],
}) {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    keyword: "",
    province: "",
    day: "",
    month: "",
    year: "",
  });

  // ฟังก์ชันตรวจสอบว่า user เป็นเจ้าของ Quote ล่าสุดหรือไม่
  const isQuoteOwner = (customer) => {
    if (!currentEmpName) return true; // ถ้าไม่มีชื่อ user ให้แสดงปุ่ม (fallback)
    
    // รวบรวม Quotes ทั้งหมดจากทุก Service
    const allQuotes = customer.Services?.flatMap((s) => 
      (s.Quotes || []).filter(q => q.Quote_No)
    ) || [];
    
    if (allQuotes.length === 0) {
      // ถ้าไม่มี Quote → แสดงปุ่มได้ (ใช้เงื่อนไข Call)
      return true;
    }
    
    // หา Quote ล่าสุด (Quote_Id มากที่สุด)
    const latestQuote = allQuotes.sort((a, b) => {
      return (b.Quote_Id || 0) - (a.Quote_Id || 0);
    })[0];
    
    // ถ้า Quote ล่าสุดมี Doc_Status = 3 (ยกเลิก) → แสดงปุ่มได้ทุกคน
    if (Number(latestQuote?.Doc_Status) === 3) {
      return true;
    }
    
    // ตรวจสอบว่า Quote ล่าสุดมี Sale_Emp_Name ตรงกับ currentEmpName หรือไม่
    if (latestQuote?.Sale_Emp_Name) {
      return String(latestQuote.Sale_Emp_Name).trim() === String(currentEmpName).trim();
    }
    
    // ถ้า Quote ไม่มี Sale_Emp_Name → แสดงปุ่มได้ (fallback)
    return true;
  };

  const [provinces, setProvinces] = useState([]);
  const [hasRestored, setHasRestored] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const tableWrapperRef = useRef(null);
  

  useEffect(() => {
    if (hasRestored) return;
    setHasRestored(true);
    const saved = localStorage.getItem("customerFilters");
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      setFilters(parsed);
    } catch (err) {}
  }, [hasRestored]);

  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const res = await api.get("/provinces");
        if (Array.isArray(res.data.data)) {
          setProvinces(res.data.data);
        } else {
          setProvinces([]);
        }
      } catch (err) {}
    };
    fetchProvinces();
  }, []);


  // Function to sync sticky columns - ensure td and th stay aligned
  const syncStickyColumns = () => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;
    
    const table = wrapper.querySelector('table');
    if (!table || !data?.length) return;
    
    // Check screen width - disable some sticky columns on small screens
    const screenWidth = window.innerWidth;
    const isSmallScreen = screenWidth < 1200; // Disable sticky on small screens
    const isVerySmallScreen = screenWidth < 768; // Disable all sticky on very small screens
    
    const thead = table.querySelector('thead');
    const tbody = table.querySelector('tbody');
    if (!thead || !tbody) return;
    
    const headerRow = thead.querySelector('tr');
    const firstRow = tbody.querySelector('tr');
    if (!headerRow || !firstRow) return;
    
    const allHeaders = Array.from(headerRow.querySelectorAll('th'));
    const firstRowCells = Array.from(firstRow.querySelectorAll('td'));
    
    // Step 1: Force table to calculate natural widths first
    void table.offsetWidth;
    void table.offsetHeight;
    
    // Step 2: Get actual column widths and sync them
    // Define max widths for text columns to prevent excessive width
    const MAX_WIDTHS = {
      'col-name': 300,        // ชื่อลูกค้า
      'col-displayName': 350, // ชื่อที่ใช้ในสัญญา
      'col-phone': 150,       // เบอร์โทรศัพท์
      'col-index': 80,        // เลขที่
    };
    
    const columnWidths = [];
    allHeaders.forEach((th, index) => {
      // Get natural width
      let headerWidth = th.getBoundingClientRect().width;
      let bodyWidth = headerWidth;
      
      if (firstRowCells[index]) {
        bodyWidth = firstRowCells[index].getBoundingClientRect().width;
      }
      
      // Use the maximum width to ensure consistency
      let maxWidth = Math.max(headerWidth, bodyWidth);
      
      // Apply max-width constraint for specific columns
      const thClass = Array.from(th.classList).find(cls => cls.startsWith('col-'));
      if (thClass && MAX_WIDTHS[thClass]) {
        maxWidth = Math.min(maxWidth, MAX_WIDTHS[thClass]);
      }
      
      columnWidths.push(maxWidth);
      
      // Force both header and body to have the same width
      th.style.width = `${maxWidth}px`;
      th.style.minWidth = `${maxWidth}px`;
      th.style.maxWidth = `${maxWidth}px`;
      
      if (firstRowCells[index]) {
        firstRowCells[index].style.width = `${maxWidth}px`;
        firstRowCells[index].style.minWidth = `${maxWidth}px`;
        firstRowCells[index].style.maxWidth = `${maxWidth}px`;
      }
    });
    
    // Step 3: Force layout recalculation with fixed widths
    void table.offsetWidth;
    void table.offsetHeight;
    void thead.offsetHeight;
    void tbody.offsetHeight;
    
    // Step 4: Calculate cumulative left positions based on fixed widths
    const calculateLeft = (targetClass) => {
      let left = 0;
      for (let i = 0; i < allHeaders.length; i++) {
        if (allHeaders[i].classList.contains(targetClass)) break;
        left += columnWidths[i] || 0;
      }
      return Math.round(left);
    };
    
    const colIndexLeft = 0;
    const colNameLeft = calculateLeft('col-name');
    const colDisplayNameLeft = calculateLeft('col-displayName');
    const colPhoneLeft = calculateLeft('col-phone');
    
    // Step 5: Apply left positions to headers with !important and ensure sticky
    const colIndexHeader = thead.querySelector('th.col-index');
    const colNameHeader = thead.querySelector('th.col-name');
    const colDisplayNameHeader = thead.querySelector('th.col-displayName');
    const colPhoneHeader = thead.querySelector('th.col-phone');
    
    // Apply sticky positioning based on screen size
    if (isVerySmallScreen) {
      // On very small screens, disable all sticky columns
      if (colIndexHeader) {
        colIndexHeader.style.setProperty('position', 'static', 'important');
        colIndexHeader.style.setProperty('left', 'auto', 'important');
      }
      if (colNameHeader) {
        colNameHeader.style.setProperty('position', 'static', 'important');
        colNameHeader.style.setProperty('left', 'auto', 'important');
      }
      if (colDisplayNameHeader) {
        colDisplayNameHeader.style.setProperty('position', 'static', 'important');
        colDisplayNameHeader.style.setProperty('left', 'auto', 'important');
      }
      if (colPhoneHeader) {
        colPhoneHeader.style.setProperty('position', 'static', 'important');
        colPhoneHeader.style.setProperty('left', 'auto', 'important');
      }
    } else if (isSmallScreen) {
      // On small screens, only keep col-index and col-name sticky
      if (colIndexHeader) {
        colIndexHeader.style.setProperty('position', 'sticky', 'important');
        colIndexHeader.style.setProperty('left', `${colIndexLeft}px`, 'important');
        colIndexHeader.style.setProperty('z-index', '26', 'important');
        void colIndexHeader.offsetHeight;
      }
      if (colNameHeader) {
        colNameHeader.style.setProperty('position', 'sticky', 'important');
        colNameHeader.style.setProperty('left', `${colNameLeft}px`, 'important');
        colNameHeader.style.setProperty('z-index', '26', 'important');
        void colNameHeader.offsetHeight;
      }
      if (colDisplayNameHeader) {
        colDisplayNameHeader.style.setProperty('position', 'static', 'important');
        colDisplayNameHeader.style.setProperty('left', 'auto', 'important');
      }
      if (colPhoneHeader) {
        colPhoneHeader.style.setProperty('position', 'static', 'important');
        colPhoneHeader.style.setProperty('left', 'auto', 'important');
      }
    } else {
      // On normal screens, keep all sticky columns
      if (colIndexHeader) {
        colIndexHeader.style.setProperty('position', 'sticky', 'important');
        colIndexHeader.style.setProperty('left', `${colIndexLeft}px`, 'important');
        colIndexHeader.style.setProperty('z-index', '26', 'important');
        void colIndexHeader.offsetHeight;
      }
      if (colNameHeader) {
        colNameHeader.style.setProperty('position', 'sticky', 'important');
        colNameHeader.style.setProperty('left', `${colNameLeft}px`, 'important');
        colNameHeader.style.setProperty('z-index', '26', 'important');
        void colNameHeader.offsetHeight;
      }
      if (colDisplayNameHeader) {
        colDisplayNameHeader.style.setProperty('position', 'sticky', 'important');
        colDisplayNameHeader.style.setProperty('left', `${colDisplayNameLeft}px`, 'important');
        colDisplayNameHeader.style.setProperty('z-index', '26', 'important');
        void colDisplayNameHeader.offsetHeight;
      }
      if (colPhoneHeader) {
        colPhoneHeader.style.setProperty('position', 'sticky', 'important');
        colPhoneHeader.style.setProperty('left', `${colPhoneLeft}px`, 'important');
        colPhoneHeader.style.setProperty('z-index', '26', 'important');
        void colPhoneHeader.offsetHeight;
      }
    }
    
    // Step 6: Apply same left positions, widths, and sticky to ALL body cells
    const allRows = tbody.querySelectorAll('tr');
    allRows.forEach((row) => {
      const cells = row.querySelectorAll('td');
      cells.forEach((cell, index) => {
        // Apply width to all cells
        if (columnWidths[index]) {
          cell.style.width = `${columnWidths[index]}px`;
          cell.style.minWidth = `${columnWidths[index]}px`;
          cell.style.maxWidth = `${columnWidths[index]}px`;
        }
        
        // Apply sticky positioning and left position for sticky cells based on screen size
        if (isVerySmallScreen) {
          // On very small screens, disable all sticky
          if (cell.classList.contains('col-index') || 
              cell.classList.contains('col-name') ||
              cell.classList.contains('col-displayName') ||
              cell.classList.contains('col-phone')) {
            cell.style.setProperty('position', 'static', 'important');
            cell.style.setProperty('left', 'auto', 'important');
          }
        } else if (isSmallScreen) {
          // On small screens, only keep col-index and col-name sticky
          if (cell.classList.contains('col-index')) {
            cell.style.setProperty('position', 'sticky', 'important');
            cell.style.setProperty('left', `${colIndexLeft}px`, 'important');
            cell.style.setProperty('z-index', '20', 'important');
          } else if (cell.classList.contains('col-name')) {
            cell.style.setProperty('position', 'sticky', 'important');
            cell.style.setProperty('left', `${colNameLeft}px`, 'important');
            cell.style.setProperty('z-index', '20', 'important');
          } else if (cell.classList.contains('col-displayName') || 
                     cell.classList.contains('col-phone')) {
            cell.style.setProperty('position', 'static', 'important');
            cell.style.setProperty('left', 'auto', 'important');
          }
        } else {
          // On normal screens, keep all sticky
          if (cell.classList.contains('col-index')) {
            cell.style.setProperty('position', 'sticky', 'important');
            cell.style.setProperty('left', `${colIndexLeft}px`, 'important');
            cell.style.setProperty('z-index', '20', 'important');
          } else if (cell.classList.contains('col-name')) {
            cell.style.setProperty('position', 'sticky', 'important');
            cell.style.setProperty('left', `${colNameLeft}px`, 'important');
            cell.style.setProperty('z-index', '20', 'important');
          } else if (cell.classList.contains('col-displayName')) {
            cell.style.setProperty('position', 'sticky', 'important');
            cell.style.setProperty('left', `${colDisplayNameLeft}px`, 'important');
            cell.style.setProperty('z-index', '20', 'important');
          } else if (cell.classList.contains('col-phone')) {
            cell.style.setProperty('position', 'sticky', 'important');
            cell.style.setProperty('left', `${colPhoneLeft}px`, 'important');
            cell.style.setProperty('z-index', '20', 'important');
          }
        }
        
        // Force reflow for each cell
        void cell.offsetHeight;
      });
    });
    
    // Step 7: Final force reflow
    void thead.offsetHeight;
    void tbody.offsetHeight;
    void table.offsetWidth;
  };

  // Reset scroll position and force reflow when data changes (after filter/search)
  useLayoutEffect(() => {
    if (tableWrapperRef.current) {
      const wrapper = tableWrapperRef.current;
      
      // Reset horizontal scroll immediately
      wrapper.scrollLeft = 0;
      
      // Use requestAnimationFrame to ensure DOM has fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (wrapper) {
            wrapper.scrollLeft = 0;
            
            const table = wrapper.querySelector('table');
            if (table) {
              // Force layout recalculation
              void wrapper.offsetHeight;
              void table.offsetHeight;
              
              // Sync sticky columns once after layout is ready
              // Use double requestAnimationFrame to ensure DOM is fully updated
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  syncStickyColumns();
                });
              });
              
              // When no data, ensure empty row doesn't affect layout
              if (data.length === 0) {
                const tbody = table.querySelector('tbody');
                if (tbody) {
                  void tbody.offsetHeight;
                  const emptyRow = tbody.querySelector('tr.empty-row');
                  if (emptyRow) {
                    const emptyCells = emptyRow.querySelectorAll('td');
                    emptyCells.forEach((cell) => {
                      if (cell.classList.contains('sticky-left') || cell.classList.contains('sticky-action')) {
                        cell.style.position = 'static';
                        cell.style.left = 'auto';
                        cell.style.right = 'auto';
                        cell.style.transform = 'none';
                        cell.style.zIndex = '1';
                      }
                    });
                    void emptyRow.offsetHeight;
                  }
                }
              }
            }
          }
        });
      });
      
      // Add scroll event listener to sync on scroll - use throttling for performance
      let scrollTimeout = null;
      const handleScroll = () => {
        if (scrollTimeout) {
          cancelAnimationFrame(scrollTimeout);
        }
        scrollTimeout = requestAnimationFrame(() => {
          syncStickyColumns();
          scrollTimeout = null;
        });
      };
      
      wrapper.addEventListener('scroll', handleScroll, { passive: true });
      
      // Add resize event listener to sync sticky columns when window is resized
      let resizeTimeout = null;
      const handleResize = () => {
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }
        resizeTimeout = setTimeout(() => {
          syncStickyColumns();
          resizeTimeout = null;
        }, 150); // Debounce resize events
      };
      
      window.addEventListener('resize', handleResize, { passive: true });
      
      return () => {
        wrapper.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
        if (scrollTimeout) {
          cancelAnimationFrame(scrollTimeout);
        }
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }
      };
    }
  }, [data, filters.province, filters.month, filters.year, tableKey]);

  useEffect(() => {
    if (!focusedArId || !tableWrapperRef.current) return;
    const wrapper = tableWrapperRef.current;
    const activeRow = wrapper.querySelector("tbody tr.active-focus-row");
    if (!activeRow) return;

    requestAnimationFrame(() => {
      activeRow.scrollIntoView({ block: "center", behavior: "auto" });
    });
  }, [focusedArId, data]);

  const handleFilterChange = (key, value) => {
    const safeValue = value && typeof value === "object" && "value" in value ? value.value : value;
    setFilters((prev) => {
      const updated = { ...prev, [key]: safeValue };
      localStorage.setItem("customerFilters", JSON.stringify(updated));
      
      // Auto filter when province is selected
      if (key === "province" && safeValue) {
        // Use requestAnimationFrame to ensure state is updated
        requestAnimationFrame(() => {
          handleFilterSubmit(updated);
        });
      }
      
      return updated;
    });
  };

  const handleFilterSubmit = (forced) => {
    const f = forced || filters;
    localStorage.setItem("customerFilters", JSON.stringify(f));
    setActiveGroup?.("all");
    
    // Reset scroll position immediately
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollLeft = 0;
    }
    
    // Force table re-render by updating key
    setTableKey(prev => prev + 1);
    
    onFilter?.(f);
  };

  const handleReset = () => {
    const cleared = { keyword: "", province: "", day: "", month: "", year: "" };
    setFilters(cleared);
    localStorage.setItem("customerFilters", JSON.stringify(cleared));
    onBranchFilterChange?.("all");
    handleFilterSubmit(cleared);
  };

  const normalizeDateTime = (call) => {
    if (!call) return null;
    // Use Call_Date_Raw for sorting if available (ISO string), fallback to Call_Date
    const dateValue = call.Call_Date_Raw || call.Call_Date;
    if (!dateValue) return null;
    
    // Check if it's already formatted (contains "/") or ISO string
    let datePart;
    if (typeof dateValue === 'string' && dateValue.includes("/")) {
      // It's formatted (DD/MM/YY), try to parse it
      try {
        const [dd, MM, yyBE] = dateValue.split(" ")[0].split("/");
        const yyyy = parseInt(yyBE) + 2500; // Approximate conversion (BE to CE)
        datePart = `${yyyy}-${MM.padStart(2, "0")}-${dd.padStart(2, "0")}`;
      } catch {
        return null;
      }
    } else {
      // It's ISO string
      datePart = dateValue.includes("T") ? dateValue.split("T")[0] : dateValue;
    }
    
    let timePart = call.Time_Start || "";
    if (timePart.includes("T")) timePart = timePart.split("T")[1];
    if (timePart.length >= 5) timePart = timePart.substring(0, 5);
    if (!timePart) timePart = "00:00";
    return new Date(`${datePart}T${timePart}`);
  };

  const getLatestCall = (row) => {
    if (!row.Services || row.Services.length === 0) return null;
    // Filter calls that have either Call_Date or Call_Date_Raw
    const calls = row.Services.map((s) => s.LatestCall).filter((c) => c && (c.Call_Date || c.Call_Date_Raw));
    if (calls.length === 0) return null;
    return calls.sort((a, b) => normalizeDateTime(b) - normalizeDateTime(a))[0];
  };

  const handleSort = (field) => {
    let order = "asc";
    if (sortField === field && sortOrder === "asc") {
      order = "desc";
    }
    setSortField(field);
    setSortOrder(order);
    onFilter({
      ...filters,
      sortField: field,
      sortOrder: order,
    });
  };

  const provinceOptions = useMemo(() => {
    return provinces.map((p) => ({
      value: String(p.ProvinceId),
      label: p.NameInThai,
    }));
  }, [provinces]);

  const handleEnterKey = (e) => {
    if (e.key === "Enter") {
      setActiveGroup?.("all");
      handleFilterSubmit();
    }
  };

  const NoIndicators = {
    IndicatorsContainer: () => null,
    DropdownIndicator: () => null,
  };

  return (
    <div
      className="card table-card border-0 rounded-0 shadow-sm"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        padding: "8px 12px",
      }}
    >
      <style>{`
        * { font-size: 14px !important; }

        /* กล่องแต่ละ cell ชิดกันมาก */
        .table-wrapper-scroll {
          position: relative;
          isolation: isolate;
          scroll-behavior: auto;
          overflow-x: auto !important;
          overflow-y: auto !important;
        }

        table.table {
          border-collapse: separate !important;
          border-spacing: 1px !important;   /* ระยะห่างระหว่างกล่อง */
          width: 100%;
          min-width: 1300px;
          table-layout: auto;
          position: relative;
          transform: translateZ(0);
        }

        th, td {
          text-align: center !important;
          vertical-align: middle !important;
          padding: 8px 10px !important;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          /* ไม่ต้องมี grid */
          border: none !important;

          /* กล่องเรียบ ไม่มีโค้ง ไม่มีเงา */
          background: white;
          border-radius: 0 !important;

          /* ⭐ เส้นรอบกล่องแบบเรียบ ๆ */
          outline: 1px solid #d0d0d0;
          outline-offset: -1px;
        }
        
        /* Allow text wrapping for long names with ellipsis */
        td.col-name, td.col-displayName {
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          max-width: 100%;
        }
        
        th.col-name, th.col-displayName {
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          max-width: 100%;
        }

        /* Header กล่องสีแดงแบบเรียบ ไม่มีเงา */
        thead th {
          background-color: #E13F57 !important;
          color: white !important;
          font-weight: 600;
          border-radius: 0 !important;
          position: sticky !important;
          top: 0 !important;
          z-index: 2 !important;

          outline: 1px solid #b83c52 !important;
          outline-offset: -1px;
        }

        tbody tr:hover td {
          background: #f5f7ff !important;
        }

        tbody tr.active-focus-row td {
          background: #fce8eb !important;
        }

        /* Sticky Columns แบบกล่องเรียบ ไม่มีเงา */
        td.sticky-left,
        td.sticky-left-2,
        td.sticky-left-3 {
          position: sticky;
          background: white !important;
          z-index: 100;
          border-radius: 0 !important;
          will-change: transform;
          transform: translateZ(0);
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          isolation: isolate;

          outline: 1px solid #d0d0d0;
          outline-offset: -1px;
        }

        th.sticky-left,
        th.sticky-left-2,
        th.sticky-left-3 {
          position: sticky;
          background: white !important;
          z-index: 101;
          border-radius: 0 !important;

          outline: 1px solid #d0d0d0;
          outline-offset: -1px;
        }

        thead th.sticky-left {
          background-color: #E13F57 !important;
          z-index: 101 !important;
          position: sticky !important;
        }

        /* Sticky positioning will be set by JavaScript based on actual column widths */
        /* Force sticky positioning for all sticky-left columns - left will be set by JS */
        th.col-index, td.col-index { 
          position: sticky !important;
        }
        th.col-name, td.col-name { 
          position: sticky !important;
        }
        th.col-displayName, td.col-displayName { 
          position: sticky !important;
        }
        th.col-phone, td.col-phone { 
          position: sticky !important;
        }

        /* Override for header to ensure it works - left will be set by JS */
        thead th.col-index {
          position: sticky !important;
          z-index: 101 !important;
          background-color: #E13F57 !important;
        }
        thead th.col-name {
          position: sticky !important;
          z-index: 101 !important;
          background-color: #E13F57 !important;
        }
        thead th.col-displayName {
          position: sticky !important;
          z-index: 101 !important;
          background-color: #E13F57 !important;
          transform: translateZ(0) !important;
          will-change: transform !important;
        }
        thead th.col-phone {
          position: sticky !important;
          z-index: 101 !important;
          background-color: #E13F57 !important;
          transform: translateZ(0) !important;
          will-change: transform !important;
        }

        /* When table is empty, disable all sticky positioning */
        table.table-empty thead th.sticky-left,
        table.table-empty thead th.sticky-action,
        table.table-empty tbody td.sticky-left,
        table.table-empty tbody td.sticky-action {
          position: static !important;
          left: auto !important;
          right: auto !important;
          transform: none !important;
          z-index: auto !important;
        }

        /* Force sticky positioning for header even when table is empty */
        table.table-empty thead th.col-index {
          position: sticky !important;
          left: 0px !important;
          z-index: 101 !important;
          background-color: #E13F57 !important;
        }

        table.table-empty thead th.col-name {
          position: sticky !important;
          left: 70px !important;
          z-index: 101 !important;
          background-color: #E13F57 !important;
        }


        /* sticky action ต้องมีกรอบเรียบ ๆ ไม่มีเงา */
        td.sticky-action, th.sticky-action {
          position: sticky;
          right: 0;
          background: white !important;
          z-index: 20;
          border-radius: 0 !important;
          will-change: transform;
          transform: translateZ(0);
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          isolation: isolate;

          outline: 1px solid #d0d0d0;
          outline-offset: -1px;
        }

        thead th.sticky-action {
          background-color: #E13F57 !important;
          z-index: 21 !important;
        }

        td.call-result-col,
        th.call-result-col {
          min-width: 450px !important;
          max-width: 600px !important;
          white-space: normal !important;
        }

        /* ถ้ามีผลการโทร → ชิดซ้าย */
        td.call-result-col {
          text-align: left !important;
          padding-left: 12px !important;
          white-space: normal !important;
        }

        /* ถ้ายังไม่มีผลการโทร → กลับมาตรงกลาง */
        td.call-result-col.empty {
          text-align: center !important;
        }

        /* Empty state - ensure table structure is maintained */
        tbody tr.empty-row td.empty-cell {
          position: static !important;
          left: auto !important;
          right: auto !important;
          transform: none !important;
          z-index: 1 !important;
          background: white !important;
          outline: 1px solid #d0d0d0;
          outline-offset: -1px;
        }

        /* Empty row sticky cells should not be sticky */
        tbody tr.empty-row td.sticky-left,
        tbody tr.empty-row td.sticky-action {
          position: static !important;
          left: auto !important;
          right: auto !important;
          transform: none !important;
          z-index: 1 !important;
          background: white !important;
        }

        /* Empty message cell - spans multiple columns */
        tbody tr.empty-row td.empty-message-cell {
          text-align: center !important;
          padding: 16px !important;
          width: 100% !important;
        }

        /* Ensure empty row doesn't break table layout */
        tbody tr.empty-row {
          display: table-row;
        }

        /* Force table to maintain min-width even when empty */
        table.table-empty {
          min-width: 1300px !important;
        }

        /* Ensure thead is always rendered correctly */
        thead {
          display: table-header-group !important;
        }

        /* Ensure sticky header columns maintain position when table has data */
        thead th.sticky-left,
        thead th.sticky-action {
          position: sticky !important;
        }
        
        thead th.sticky-left {
          z-index: 101 !important;
        }
        
        thead th.sticky-action {
          z-index: 101 !important;
        }
          
        .filter-control::placeholder {
          font-size: 14px !important;
          opacity: .7; /* จะให้เข้มขึ้นหรือจางลงก็ปรับตรงนี้ */
        }

        .no-arrow {
          appearance: none !important;
          -moz-appearance: none !important;
          -webkit-appearance: none !important;
          background-image: none !important;
        }

      `}</style>


      <div
        className="d-flex align-items-center justify-content-between gap-2 px-3 pt-2 pb-1 flex-wrap"
      >
        <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">

          {/* INPUT ค้นหา (ปกติ unchanged) */}
          <div className="position-relative" style={{ width: "200px", height: "42px" }}>
            <input
              type="text"
              placeholder="ค้นหา..."
              value={filters.keyword}
              onChange={(e) => handleFilterChange("keyword", e.target.value)}
              onKeyDown={handleEnterKey}
              className="form-control form-control-sm filter-control"
              style={{ height: "42px" }}
            />
            {filters.keyword && (
              <button
                onClick={() => {
                  const newFilters = { ...filters, keyword: "" };
                  setFilters(newFilters);
                  localStorage.setItem("customerFilters", JSON.stringify(newFilters));
                  handleFilterSubmit(newFilters);
                }}
                className="position-absolute bg-transparent border-0 p-0"
                style={{
                  right: "18px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 5,
                }}
              >
                <Icon icon="mdi:close" width="12" height="12" className="text-muted" />
              </button>
            )}
          </div>

          {/* SEARCHABLE SELECT จังหวัด */}
          <div className="position-relative" style={{ width: "200px", height: "42px" }}>
            <SearchableSelect
              value={filters.province}
              onChange={(val) => handleFilterChange("province", val)}
              options={provinceOptions}
              placeholder="จังหวัด"
              searchPlaceholder="พิมพ์ค้นหา..."
              classNamePrefix="react-select"
              className="filter-select"
              hideArrow={!!filters.province}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              components={filters.province ? NoIndicators : undefined}   // ⭐ ซ่อน dropdown-arrow เมื่อมีค่า
            />

            {filters.province && (
              <button
                onClick={() => {
                  const newFilters = { ...filters, province: "" };
                  setFilters(newFilters);
                  localStorage.setItem("customerFilters", JSON.stringify(newFilters));
                  handleFilterSubmit(newFilters);
                }}
                className="position-absolute bg-transparent border-0 p-0"
                style={{
                  right: "18px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 20,
                }}
              >
                <Icon icon="mdi:close" width="12" height="12" className="text-muted" />
              </button>
            )}
          </div>

          <div className="position-relative" style={{ width: "200px", height: "42px" }}>
            <SearchableSelect
              value={branchFilter}
              onChange={(value) => onBranchFilterChange?.(String(value))}
              options={[
                { value: "all", label: "สาขา: ทั้งหมด" },
                ...branches.map((b) => ({
                  value: String(b.Branch_Id),
                  label: b.Branch_Name,
                })),
              ]}
              placeholder="สาขา"
              searchPlaceholder="พิมพ์ค้นหา..."
              classNamePrefix="react-select"
              className="filter-select"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          <span>ระยะหมดสัญญา</span>

          {/* SELECT เดือน */}
          <div className="position-relative" style={{ width: "100px", height: "42px" }}>
            <select
              value={filters.month}
              onChange={(e) => {
                const val = e.target.value;
                const newFilters = { ...filters, month: val };
                setFilters(newFilters);
                localStorage.setItem("customerFilters", JSON.stringify(newFilters));
              }}
              className={`form-select form-select-sm filter-control ${filters.month ? "no-arrow" : ""}`}
              style={{ paddingRight: "24px", height: "42px" }}
            >
              <option value="">เดือน</option>
              {["ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."].map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>

            {filters.month && (
              <button
                onClick={() => {
                  const newFilters = { ...filters, month: "" };
                  setFilters(newFilters);
                  localStorage.setItem("customerFilters", JSON.stringify(newFilters));
                  handleFilterSubmit(newFilters);
                }}
                className="position-absolute bg-transparent border-0 p-0"
                style={{ right: "18px", top: "50%", transform: "translateY(-50%)", zIndex: 5 }}
              >
                <Icon icon="mdi:close" width="12" height="12" className="text-muted" />
              </button>
            )}
          </div>

          {/* SELECT ปี */}
          <div className="position-relative" style={{ width: "100px", height: "42px" }}>
            <select
              value={filters.year ? parseInt(filters.year) + 543 : ""}
              onChange={(e) => {
                const val = e.target.value;
                const adYear = val ? parseInt(val) - 543 : "";
                const newFilters = { ...filters, year: adYear || "" };
                setFilters(newFilters);
                localStorage.setItem("customerFilters", JSON.stringify(newFilters));
              }}
              className={`form-select form-select-sm filter-control ${filters.year ? "no-arrow" : ""}`}
              style={{ paddingRight: "24px", height: "42px" }}
            >
              <option value="">ปี (พ.ศ.)</option>
              {(() => {
                const currentBE = new Date().getFullYear() + 543;
                const years = [];
                for (let i = currentBE - 5; i <= currentBE + 5; i++) years.push(i);
                return years.map((y) => <option key={y} value={y}>{y}</option>);
              })()}
            </select>

            {filters.year && (
              <button
                onClick={() => {
                  const newFilters = { ...filters, year: "" };
                  setFilters(newFilters);
                  localStorage.setItem("customerFilters", JSON.stringify(newFilters));
                  handleFilterSubmit(newFilters);
                }}
                className="position-absolute bg-transparent border-0 p-0"
                style={{ right: "18px", top: "50%", transform: "translateY(-50%)", zIndex: 5 }}
              >
                <Icon icon="mdi:close" width="12" height="12" className="text-muted" />
              </button>
            )}
          </div>

          {/* ปุ่มเคลียร์ค่า */}
          <button
            onClick={handleReset}
            className="btn btn-outline-secondary btn-sm"
            style={{ height: "42px" }}
          >
            เคลียร์ค่า
          </button>

          {/* ปุ่มค้นหา */}
          <button
            onClick={() => handleFilterSubmit()}
            className="p-0 border-0 bg-transparent d-flex align-items-center justify-content-center"
            style={{ width: "32px", height: "42px" }}
          >
            <Icon icon="mdi:magnify" width="20" height="20" className="text-gray-600 hover:text-[#E13F57]" />
          </button>

        </div>
      </div>

      <div
        ref={tableWrapperRef}
        className="table-wrapper-scroll flex-1"
        style={{ overflowY: "auto", overflowX: "auto", flexGrow: 1, minHeight: 0, padding: "0" }}
      >
        <table 
          key={`table-${tableKey}-${filters.province}-${filters.month}-${filters.year}`}
          className={`table table-bordered table-hover mb-0 ${!data?.length ? 'table-empty' : ''}`}
        >
          <thead>
            <tr>
              <th
                className="sticky-left col-index"
                style={{ 
                  cursor: "pointer",
                  ...(data?.length ? {
                    position: "sticky",
                    left: "0px",
                    zIndex: 26,
                    backgroundColor: "#E13F57"
                  } : {
                    position: "static",
                    left: "auto"
                  })
                }}
                onClick={() => handleSort("index")}
              >
                ลำดับ {sortField === "index" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>

              <th
                className="sticky-left col-name"
                style={{ 
                  cursor: "pointer",
                  ...(data?.length ? {
                    position: "sticky",
                    zIndex: 26,
                    backgroundColor: "#E13F57"
                  } : {
                    position: "static",
                    left: "auto"
                  })
                }}
                onClick={() => handleSort("name")}
              >
                ชื่อลูกค้า {sortField === "name" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>

              <th
                className={`sticky-left col-displayName ${data?.length ? 'has-data' : ''}`}
                style={{ 
                  cursor: "pointer",
                  ...(data?.length ? {
                    position: "sticky",
                    zIndex: 26,
                    backgroundColor: "#E13F57"
                  } : {
                    position: "static",
                    left: "auto"
                  })
                }}
                onClick={() => handleSort("displayName")}
              >
                ชื่อลูกค้าในสัญญา {sortField === "displayName" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>

              <th
                className={`sticky-left col-phone ${data?.length ? 'has-data' : ''}`}
                style={{ 
                  cursor: "pointer",
                  ...(data?.length ? {
                    position: "sticky",
                    zIndex: 26,
                    backgroundColor: "#E13F57"
                  } : {
                    position: "static",
                    left: "auto"
                  })
                }}
                onClick={() => handleSort("phone")}
              >
                เบอร์โทรศัพท์ {sortField === "phone" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("owner")}>
                ชื่อผู้ติดต่อ {sortField === "owner" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("position")}>
                ตำแหน่ง {sortField === "position" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("callResult")}>
                ผลการโทรล่าสุด {sortField === "callResult" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("callDate")}>
                วันที่นัดหมายโทรหา {sortField === "callDate" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("quote")}>
                ใบเสนอราคา {sortField === "quote" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>

              <th 
                className="sticky-action"
                style={{
                  cursor: "pointer",
                  ...(data?.length ? {
                    position: "sticky",
                    right: "0",
                    zIndex: 26,
                    backgroundColor: "#E13F57"
                  } : {
                    position: "static",
                    right: "auto"
                  })
                }}
              >
                การจัดการ
              </th>
            </tr>
          </thead>

          <tbody>
            {data?.length ? (
              data.map((row, idx) => {
                const contact = row.Contacts?.[0];
                const latestCall = getLatestCall(row);

                let allQuotes = [];
                row.Services?.forEach((s) => {
                  if (Array.isArray(s.Quotes)) allQuotes.push(...s.Quotes);
                });

                const latestQuote =
                  allQuotes.length > 0
                    ? allQuotes.reduce((latest, q) =>
                        q.Quote_Id > latest.Quote_Id ? q : latest
                      )
                    : null;

                return (
                  <tr
                    key={row.AR_Id}
                    className={
                      String(row.AR_Id) === String(focusedArId || "")
                        ? "active-focus-row"
                        : ""
                    }
                  >
                    <td className="sticky-left col-index">
                      {(currentPage - 1) * itemsPerPage + (idx + 1)}
                    </td>

                    <td 
                      className="sticky-left col-name text-start fw-bold text-primary"
                      title={`${row.AR_Name} ${row.AR_LastName || ""}`.trim()}
                    >
                      <Link
                        to={`/EditCustomer/${row.AR_Id}`}
                        onClick={() => onFocusCustomer?.(row.AR_Id)}
                        className="text-decoration-underline"
                      >
                        {row.AR_Name} {row.AR_LastName || ""}
                      </Link>
                    </td>

                    <td 
                      className="sticky-left col-displayName text-start"
                      title={row.DisplayName || "-"}
                    >
                      {row.DisplayName || "-"}
                    </td>

                    <td className="sticky-left col-phone">
                      {contact?.AR_Tel ? (
                        <>
                          {(() => {
                            const formatted = formatThaiPhone(contact.AR_Tel);

                            return (
                              <div className="d-flex align-items-center justify-content-center gap-1">
                                <span>{formatted}</span>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 m-0 text-secondary"
                                  onClick={() => {
                                    navigator.clipboard.writeText(contact.AR_Tel);
                                    Swal.fire({
                                      icon: "success",
                                      title: "คัดลอกแล้ว!",
                                      timer: 1000,
                                      showConfirmButton: false,
                                    });
                                  }}
                                >
                                  <Icon icon="mdi:content-copy" width="16" />
                                </button>
                              </div>
                            );
                          })()}
                          {contact.AR_ContactName && (
                            <small className="text-muted d-block">
                              ({contact.AR_ContactName})
                            </small>
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td className="text-start">
                      {row.AR_Group === 1
                        ? `${row.AR_Name || ""} ${row.AR_LastName || ""}`.trim() || "-"
                        : contact?.AR_ContactName || "-"}
                    </td>

                    <td className="text-start">
                      {contact?.AR_ContactPosition || "-"}
                    </td>

                    <td
                      className={`call-result-col ${latestCall?.Call_Result ? "" : "empty"}`}
                    >

                      {latestCall?.Call_Result ? (
                        latestCall.Call_Result
                      ) : (
                        <span className="px-3 py-1 rounded-full text-white" style={{ backgroundColor: "#E13F57" }}>
                          ยังไม่มีผลการโทร
                        </span>
                      )}
                    </td>

                    <td>
                      {(() => {
                        const c = latestCall;
                        if (!c) return "-";
                        
                        // Use Call_Date if it's formatted (contains "/"), otherwise format from Call_Date_Raw
                        let dStr = c.Call_Date;
                        
                        // Check if Call_Date is already formatted (mobile format: "DD/MM/YY HH:mm")
                        if (dStr && typeof dStr === 'string' && dStr.includes("/")) {
                          // Already formatted, use it directly
                          if (c.Call_Result) {
                            // Has result: return formatted date with time
                            return dStr;
                          } else {
                            // No result: append Time_Start - Time_End if available
                            const getTime = (val) => {
                              if (!val) return "";
                              if (typeof val === "string" && val.includes("T"))
                                return val.substring(11, 16);
                              if (typeof val === "string" && val.length >= 5)
                                return val.substring(0, 5);
                              return "";
                            };
                            const start = getTime(c.Time_Start);
                            const end = getTime(c.Time_End);
                            if (start && end) {
                              // Extract date part from formatted string
                              const datePart = dStr.split(" ")[0];
                              return `${datePart} ${start} - ${end}`;
                            }
                            return dStr;
                          }
                        }
                        
                        // Not formatted, use Call_Date_Raw or Call_Date (ISO string)
                        dStr = c.Call_Date_Raw || c.Call_Date;
                        if (!dStr) return "-";
                        
                        const [datePart, timePartFull] = dStr.split("T");
                        if (!datePart) return "-";
                        const dd = datePart.substring(8, 10);
                        const MM = datePart.substring(5, 7);
                        const yyyy = parseInt(datePart.substring(0, 4)) + 543;
                        const time = timePartFull ? timePartFull.substring(0, 5) : "00:00";

                        if (c.Call_Result) {
                          return `${dd}/${MM}/${yyyy} ${time}`;
                        }

                        const getTime = (val) => {
                          if (!val) return "";
                          if (typeof val === "string" && val.includes("T"))
                            return val.substring(11, 16);
                          if (typeof val === "string" && val.length >= 5)
                            return val.substring(0, 5);
                          return "";
                        };

                        const start = getTime(c.Time_Start) || time;
                        const end = getTime(c.Time_End) || start;
                        return `${dd}/${MM}/${yyyy} ${start} - ${end}`;
                      })()}
                    </td>

                    <td>
                      {latestQuote ? (
                        <Link
                          to={`/quote/edit/${latestQuote.Quote_Id}?AR_id=${row.AR_Id}`}
                          onClick={() => onFocusCustomer?.(row.AR_Id)}
                          className="btn btn-sm text-white d-flex align-items-center justify-content-center gap-1"
                          style={{ backgroundColor: "#F17F25" }}
                        >
                          <Icon icon="mdi:file-document-outline" width="16" />
                          {latestQuote.Quote_No}
                        </Link>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    <td className="sticky-action">
                      <div className="d-flex justify-content-center gap-2 flex-nowrap">
                        <button
                          onClick={() => {
                            const services = row.Services || [];
                            const firstService = services[0] || {};
                            onOpenContactModal?.(
                              {
                                AR_Id: row.AR_Id,
                                ServiceAddr_Id: firstService?.ServiceAddr_Id || null,
                                Comp_Id: row.Comp_Id,
                              },
                              "result"
                            );
                          }}
                          disabled={!isQuoteOwner(row)}
                          className="btn btn-sm text-white"
                          style={{ 
                            backgroundColor: isQuoteOwner(row) ? "#598AE4" : "#cccccc",
                            cursor: isQuoteOwner(row) ? "pointer" : "not-allowed",
                            opacity: isQuoteOwner(row) ? 1 : 0.6
                          }}
                          title={!isQuoteOwner(row) ? "เฉพาะเจ้าของใบเสนอราคาล่าสุดเท่านั้นที่สามารถบันทึกการโทรได้" : ""}
                        >
                          บันทึกการโทร
                        </button>

                        <button
                          onClick={() => {
                            onOpenContactModal?.(
                              {
                                AR_Id: row.AR_Id,
                                ServiceAddr_Id: null,
                                Comp_Id: row.Comp_Id,
                                services: row.Services || [],
                              },
                              "appointment"
                            );
                          }}
                          disabled={!isQuoteOwner(row)}
                          className="btn btn-sm text-white"
                          style={{ 
                            backgroundColor: isQuoteOwner(row) ? "#7759E4" : "#cccccc",
                            cursor: isQuoteOwner(row) ? "pointer" : "not-allowed",
                            opacity: isQuoteOwner(row) ? 1 : 0.6
                          }}
                          title={!isQuoteOwner(row) ? "เฉพาะเจ้าของใบเสนอราคาล่าสุดเท่านั้นที่สามารถนัดหมายได้" : ""}
                        >
                          นัดหมาย
                        </button>

                        <Link
                          to={`/EditCustomer/${row.AR_Id}`}
                          onClick={() => onFocusCustomer?.(row.AR_Id)}
                          className="btn btn-light btn-sm d-flex align-items-center gap-1"
                        >
                          <Icon icon="mdi:eye" width="18" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="empty-row">
                <td 
                  colSpan="10"
                  className="empty-cell text-center py-4 text-muted empty-message-cell"
                  style={{ 
                    position: "static", 
                    left: "auto", 
                    right: "auto",
                    transform: "none",
                    zIndex: 1
                  }}
                >
                  ไม่มีข้อมูลลูกค้า
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="p-2 border-top bg-white" style={{ flexShrink: 0 }}>
        <Pagination
          totalItems={totalRecords}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      </div>

      <ServiceLocationModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        arId={selectedLocation?.AR_Id}
        services={selectedLocation?.services || []}
        onSelect={(loc) => {
          onOpenContactModal?.(
            {
              AR_Id: selectedLocation?.AR_Id,
              ServiceAddr_Id: loc.ServiceAddr_Id,
              Comp_Id: selectedLocation?.Comp_Id,
            },
            selectedLocation?.type
          );
          setIsLocationModalOpen(false);
        }}
      />
    </div>
  );
}

